import { useMemo } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import type { BurndownPoint } from '../../hooks/useSprintTelemetry';
import type { SprintEpic, IssueExecution } from '../../api/sprint';
import type { TelemetryEvent } from '../../api/telemetry';
import type { SprintPlan } from '../../api/sprint';
import SprintKPIRow from '../telemetry/SprintKPIRow';
import BurndownChart from '../telemetry/BurndownChart';
import SprintLifecycleTimeline from '../telemetry/SprintLifecycleTimeline';
import { useSpriteActivity } from '../../api/telemetry';
import { SprintBuildHistory } from './SprintBuildHistory';
import { FileActivityPanel } from './FileActivityPanel';
import { QualityGateMatrix } from './QualityGateMatrix';
import { TokenUsageCard } from './TokenUsageCard';
import { VerificationSummaryCard } from './VerificationSummaryCard';
import { TestEvidenceCard } from './TestEvidenceCard';
import { PostBuildEvidenceCard } from './PostBuildEvidenceCard';

interface Props {
  sprint: any;
  epics: SprintEpic[];
  issueExecutions: IssueExecution[];
  sprites: any[];
  burndownData: BurndownPoint[];
  telemetryEvents: TelemetryEvent[];
  sprintPhases: TelemetryEvent[];
  errorSummary: any;
  sprintPlan?: SprintPlan | null;
  qualityGates: { gates: string[]; byStory: Record<string, Record<string, 'pass' | 'fail' | 'skipped'>> };
  tokenUsage: {
    totalInput: number;
    totalOutput: number;
    totalCacheRead: number;
    total: number;
    byStory: Record<string, { input: number; output: number; cacheRead: number; total: number }>;
    sessionCount: number;
  };
}

const statusDotColor: Record<string, string> = {
  pending: 'bg-neutral-400',
  running: 'bg-coral-500',
  success: 'bg-accent-500',
  failure: 'bg-red-500',
};

interface VerificationBadge {
  lint: 'pass' | 'fail' | 'skip';
  typecheck: 'pass' | 'fail' | 'skip';
  unitTests: 'pass' | 'fail' | 'skip';
  unitTestsPassed?: number;
  unitTestsFailed?: number;
}

function VerifyChip({ label, status, detail }: { label: string; status: 'pass' | 'fail' | 'skip'; detail?: string }) {
  if (status === 'skip') return null;
  const colors = status === 'pass'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-700 border-red-200';
  const icon = status === 'pass' ? '✓' : '✗';

  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-px rounded border ${colors}`}>
      <span className="opacity-70">{icon}</span>
      {label}
      {detail && <span className="font-mono tabular-nums">{detail}</span>}
    </span>
  );
}

function SpriteActivityTicker({ batchId }: { batchId: string }) {
  const { data } = useSpriteActivity(batchId, { limit: 20 });
  const events = data?.events?.slice(0, 5) || [];
  if (events.length === 0) return null;

  return (
    <div className="bg-neutral-50/60 border border-neutral-100 rounded-lg px-4 py-2 mt-3 flex items-center gap-3 overflow-hidden">
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="w-2 h-2 rounded-full bg-coral-500 animate-pulse" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-coral-500">Live</span>
      </div>
      <div className="font-mono text-[10px] text-neutral-500 truncate flex-1">
        {events.map((e: any, i: number) => {
          const label = e.issue_id || e.story_id || '';
          const detail = e.file_path
            ? `created ${e.file_path.split('/').pop()}`
            : e.event_type.replace('sprite_', '').replace(/_/g, ' ');
          return (
            <span key={e.id || i}>
              {i > 0 && <span className="mx-1.5 text-neutral-300">&middot;</span>}
              {label && <span className="font-semibold text-neutral-600">{label}</span>}{' '}
              {detail}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function MonitorOverviewTab({ sprint, epics, issueExecutions, sprites, burndownData, telemetryEvents, sprintPhases, errorSummary, sprintPlan, qualityGates, tokenUsage }: Props) {
  const { project } = useProject();

  // Authoritative story count: plan has the full manifest, issueExecutions only has started stories
  const totalStories = Math.max(sprintPlan?.totalStories || 0, issueExecutions.length);

  const storySummary = useMemo(() => {
    const statusOrder: Record<string, number> = { running: 0, pending: 1, failure: 2, success: 3 };
    return [...issueExecutions].sort((a, b) => (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4));
  }, [issueExecutions]);

  // Build per-story verification lookup from telemetry events
  const storyVerification = useMemo(() => {
    const map = new Map<string, VerificationBadge>();
    for (const e of telemetryEvents) {
      if (e.eventType !== 'story_execution_completed' && e.eventType !== 'sprite_verification_completed') continue;
      const payload = e.payload as Record<string, unknown> | null;
      const v = payload?.verification as VerificationBadge | undefined;
      const storyId = (payload?.storyId as string) || e.issueId;
      if (v && storyId) {
        map.set(storyId, v);
      }
    }
    return map;
  }, [telemetryEvents]);

  return (
    <div className="space-y-5">
      <SprintKPIRow
        issueExecutions={issueExecutions}
        epics={epics}
        sprites={sprites}
        startedAt={sprint?.startedAt}
        completedAt={sprint?.completedAt}
        failedAt={sprint?.failedAt}
        sprintStatus={sprint?.status}
        telemetryEvents={telemetryEvents}
        topErrorMessage={errorSummary?.topError?.errorText}
        batchId={sprint?.batchId}
        totalStoriesOverride={totalStories}
      />

      <SprintLifecycleTimeline events={sprintPhases} />
      <BurndownChart
        data={burndownData}
        totalStories={totalStories}
        startTime={sprint?.startedAt ? new Date(sprint.startedAt).getTime() : undefined}
        telemetryEvents={telemetryEvents}
        epics={epics}
        issueExecutions={issueExecutions}
      />

      {sprint?.batchId && sprint.status === 'building' && (
        <SpriteActivityTicker batchId={sprint.batchId} />
      )}

      {/* Verification + Evidence + Quality Gates + Tokens — 2x2 grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <VerificationSummaryCard telemetryEvents={telemetryEvents} />
        <TestEvidenceCard testResults={sprint?.testResults} />
        <QualityGateMatrix gates={qualityGates.gates} byStory={qualityGates.byStory} />
        <TokenUsageCard
          totalInput={tokenUsage.totalInput}
          totalOutput={tokenUsage.totalOutput}
          totalCacheRead={tokenUsage.totalCacheRead}
          total={tokenUsage.total}
          sessionCount={tokenUsage.sessionCount}
          byStory={tokenUsage.byStory}
        />
      </div>

      {/* Post-build evidence: build health, E2E results, screenshots */}
      <PostBuildEvidenceCard
        epicIds={epics.map(e => e.epicId).filter(Boolean)}
        batchId={sprint?.batchId}
        sprintStatus={sprint?.status}
      />

      {/* File activity */}
      <FileActivityPanel batchId={sprint?.batchId} maxHeight="360px" />

      {/* Story summary — enhanced with verification badges */}
      {storySummary.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-3">Story Summary</p>
          <div className="space-y-1">
            {storySummary.map((exec) => {
              const v = storyVerification.get(exec.issueId);
              return (
                <div key={exec.issueId} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-neutral-50 transition-colors">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotColor[exec.status] || 'bg-neutral-300'} ${exec.status === 'running' ? 'animate-pulse' : ''}`} />
                  <span className="text-[11px] font-mono text-neutral-500 shrink-0 w-16">{exec.issueId}</span>
                  <span className="text-[11px] text-neutral-700 truncate flex-1">{exec.issueTitle || exec.issueId}</span>

                  {/* Verification badges */}
                  {v && (
                    <div className="flex items-center gap-1 shrink-0">
                      <VerifyChip label="lint" status={v.lint} />
                      <VerifyChip label="tsc" status={v.typecheck} />
                      <VerifyChip
                        label="tests"
                        status={v.unitTests}
                        detail={v.unitTests !== 'skip' && (v.unitTestsPassed != null || v.unitTestsFailed != null)
                          ? ` ${v.unitTestsPassed ?? 0}/${(v.unitTestsPassed ?? 0) + (v.unitTestsFailed ?? 0)}`
                          : undefined
                        }
                      />
                    </div>
                  )}

                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                    exec.status === 'success' ? 'bg-accent-50 text-accent-600' :
                    exec.status === 'failure' ? 'bg-red-50 text-red-600' :
                    exec.status === 'running' ? 'bg-coral-50 text-coral-600' :
                    'bg-neutral-100 text-neutral-500'
                  }`}>{exec.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Build history */}
      {project?.id && (
        <SprintBuildHistory projectId={project.id} highlightSprintId={sprint?.id} />
      )}
    </div>
  );
}
